import { ASC } from "const"
import {
    SilSafetyFunction,
    SilSubsystem,
    SilResultSafetyFunction,
    SilResultSubsystem,
    SilResultSubsystemBlock,
} from "server/models"
import { SilDeviceFilter, deepCopy } from "client/utils"
import { GET, POST, DELETE, sort } from "client/store/utils"
import {
    LoadSilResultProjectDevicesByFilter,
    LoadSilResultSubsystemBlocksByFilter,
    SaveSilResultSubsystems,
    SaveSafetyFunction,
} from "client/store"

export const LoadSilResultSafetyFunctionById = (
    safetyFunctionId: number,
    projectId: number,
) => (
    GET(`/silResultSafetyFunction/id/${safetyFunctionId}/projectId/${projectId}`)
    .then((safetyFunction: SilResultSafetyFunction) => {
        // load attached project devices
        const subsystems: SilResultSubsystem[] = deepCopy(safetyFunction ? safetyFunction.subsystems : [])
        const subsystemPromiseArray: Promise<any>[] = []

        subsystems.forEach((subsystem, subsystemIndex) => {
            const blocks: SilResultSubsystemBlock[] = deepCopy(subsystem.blocks)
            const blockPromiseArray: Promise<any>[] = []

            blocks.forEach((block, blockIndex) => {
                const filters: SilDeviceFilter = {
                    projectId,
                    deviceId: block.deviceId,
                    projectDeviceId: block.projectDeviceId,
                    qidProductId: block.qidProductId,
                    qidModelId: block.qidModelId,
                    qidSafetyFunctionId: block.qidSafetyFunctionId,
                    qidOptionId: block.qidOptionId,
                    qidDatapointId: block.qidDatapointId,
                    qidMethod: block.qidMethod,
                }

                blockPromiseArray.push(
                    LoadSilResultProjectDevicesByFilter(filters).then((projectDevices) => {
                        if (projectDevices.length === 1) {
                            blocks[blockIndex] = {
                                ...blocks[blockIndex],
                                projectDevice: projectDevices[0],
                            }
                        }
                    }),
                )
            })

            subsystemPromiseArray.push(
                Promise.all(blockPromiseArray).then(() => {
                    subsystems[subsystemIndex] = {
                        ...subsystems[subsystemIndex],
                        blocks,
                    }
                }),
            )
        })

        return Promise.all(subsystemPromiseArray).then(() => {
            // additional sorting for old subsystems - they have "null" value in "order" column
            if (subsystems.length > 0 && subsystems[0].order === null) {
                subsystems.sort((a, b) => sort(a, b, "id", ASC))
            }

            return {
                ...safetyFunction,
                subsystems,
            } as SilResultSafetyFunction
        })
    })
)


export const DeleteSilResultSafetyFunction = (
    safetyFunctionId: number,
    projectId: number,
) => (
    DELETE(`/silResultSafetyFunction/delete/${safetyFunctionId}/${projectId}`)
    .then((removedRows: number) => removedRows)
)

const SaveSilResultSafetyFunction = (safetyFunction: SilResultSafetyFunction) => (
    POST(
        "/silResultSafetyFunction/save",
        JSON.stringify(safetyFunction),
    ).then((res) => {
        const newSafetyFunction = res as SilResultSafetyFunction
        return newSafetyFunction.id || null
    })
)

// check, if there are functions in the project that use target device
export const CountCalculatedSafetyFunctions = (filters: SilDeviceFilter) => (
    LoadSilResultSubsystemBlocksByFilter(filters)
    .then((blocks) => {
        const safetyFunctionIds: { [key: number]: number } = {}
        blocks.forEach((block) => {
            if (block.subsystem && block.subsystem.safetyFunction && block.subsystem.safetyFunction.projectId === filters.projectId) {
                safetyFunctionIds[block.subsystem.safetyFunction.id] = block.subsystem.safetyFunction.id
            }
        })
        return Object.keys(safetyFunctionIds).length
    })
)

export const CalculateSafetyFunction = (
    safetyFunction: SilSafetyFunction,
    subsystemList: readonly SilSubsystem[],
) => (
    DeleteSilResultSafetyFunction(safetyFunction.id, safetyFunction.projectId).then(() => {
        const resultSafetyFunction: SilResultSafetyFunction = {
            id: safetyFunction.id,
            projectId: safetyFunction.projectId,
            calculationMethodDangerous: safetyFunction.calculationMethodDangerous,
            calculationMethodSafe: safetyFunction.calculationMethodSafe,
            mode: safetyFunction.mode,
            architecturalConstraints: safetyFunction.architecturalConstraints,
            targetSil: safetyFunction.targetSil,
            targetPfd: safetyFunction.targetPfd,
            targetStl: safetyFunction.targetStl,
            targetStr: safetyFunction.targetStr,
        }

        return SaveSilResultSafetyFunction(resultSafetyFunction).then(() => (
            SaveSilResultSubsystems(safetyFunction.id, safetyFunction.projectId, subsystemList).then(() => {
                const calculatedFunction = { ...safetyFunction }
                delete calculatedFunction.createdAt
                delete calculatedFunction.updatedAt
                calculatedFunction.calculated = true

                return SaveSafetyFunction(calculatedFunction.id, calculatedFunction)
            })
        ))
    })
)
